import type { PaymentMethodType, PlanCategory } from '@mentimeter/http-clients';

export enum PaymentProcessor {
  OTHER = '',
  UNKNOWN = 'unknown',
  VISA = 'visa',
  MASTERCARD = 'master-card',
  AMERICAN_EXPRESS = 'american-express',
  DISCOVER = 'discover',
  MAESTRO = 'maestro',
  UNION_PAY = 'unionpay',
}

export enum CurrencySymbol {
  EUR = '€',
  GBP = '£',
  USD = '$',
  SEK = 'kr',
}

export enum CurrencyCode {
  EUR = 'EUR',
  GBP = 'GBP',
  USD = 'USD',
  SEK = 'SEK',
}

export enum DecimalSeparator {
  COMMA = ',',
  POINT = '.',
}

export enum ThousandSeparator {
  COMMA = ',',
  POINT = '.',
  SPACE = ' ',
}

export enum SymbolPosition {
  LEADING,
  TRAILING,
}

export enum SymbolSeparator {
  SPACE = ' ',
}

export interface PricingFormat {
  decimalSeparator: DecimalSeparator;
  thousandSeparator: ThousandSeparator;
  symbolPosition: SymbolPosition;
  symbolSeparator?: SymbolSeparator;
}

interface MonthlyPlansT {
  [PlanCategory.BASIC_MONTHLY]: number;
  [PlanCategory.EDUCATIONAL_BASIC_MONTHLY]: number;
}

interface ConferencePlansT {
  [PlanCategory.CONFERENCE_SMALL]: number;
  [PlanCategory.CONFERENCE_LARGE]: number;
}

interface YearlyPlansT {
  [PlanCategory.BASIC]: number;
  [PlanCategory.PRO]: number;
  [PlanCategory.EDUCATIONAL_BASIC]: number;
  [PlanCategory.EDUCATIONAL_PRO]: number;
}

export type PlansT = YearlyPlansT &
  Partial<MonthlyPlansT> &
  Partial<ConferencePlansT>;

export interface PlanCacheT {
  name: string;
  category: PlanCategory;
  licenses: number;
  price: string;
  cycle: string;
  billingDate: string;
  discountPercentage?: number;
}

export interface TierT {
  unitAmount: number;
  upTo: number | null;
}

interface PlanPrice {
  amount: number | null;
  tiers: TierT[] | null;
}

export type PlanNames = keyof PlansT;

type PlansPrices = {
  [K in PlanNames]: PlanPrice;
};

export interface LocalPricingT {
  currency: {
    symbol: CurrencySymbol;
    code: CurrencyCode;
  };
  format: PricingFormat;
  otherCharges?: {
    invoiceAdminFee?: number;
  };
  plans: PlansPrices;
}

export interface PaymentMethodCardT {
  type: PaymentMethodType.Card;
  brand: string;
  expirationMonth: number;
  expirationYear: number;
  last4: string;
  isExpired: boolean;
}

interface PaymentMethodPayPalT {
  type: PaymentMethodType.PayPal;
  email: string;
}

interface PaymentMethodLinkT {
  type: PaymentMethodType.Link;
  email: string;
}

export interface PaymentMethodBankAccountT {
  type: PaymentMethodType.BankAccount;
  reference: string;
  accountName: string;
  bank: string;
  iban: string;
  swift: string;
  address: string;
}

export interface PaymentMethodVirtualAccountT {
  type: PaymentMethodType.VirtualAccount;
  reference: string;
  iban: string;
  bic: string;
  country: string;
}

export type PaymentMethodT = {
  type: PaymentMethodType;
} & (
  | PaymentMethodCardT
  | PaymentMethodPayPalT
  | PaymentMethodLinkT
  | PaymentMethodBankAccountT
  | PaymentMethodVirtualAccountT
);

export enum FormattedPlanNames {
  pro = 'Pro',
  basic = 'Basic',
  'basic-monthly' = 'Basic',
  'educational-pro' = 'Educational Pro',
  'educational-basic' = 'Educational Basic',
  'educational-basic-monthly' = 'Educational Basic',
  enterprise = 'Enterprise',
  'enterprise-trial' = 'Enterprise Trial',
}

export enum FormattedMentimeterPlanNames {
  pro = 'Mentimeter Pro',
  basic = 'Mentimeter Basic',
  'basic-monthly' = 'Mentimeter Basic',
  'educational-pro' = 'Mentimeter Educational Pro',
  'educational-basic' = 'Mentimeter Educational Basic',
  'educational-basic-monthly' = 'Mentimeter Educational Basic',
  'conference-small' = 'Mentimeter Conference Small',
  'conference-large' = 'Mentimeter Conference Large',
  enterprise = 'Mentimeter Enterprise',
  'enterprise-trial' = 'Mentimeter Enterprise Trial',
}

export interface TaxIdT {
  tax_id_type?: string;
  tax_id_value?: string;
  tax_id_country?: string;
}

export interface AddressFieldsT {
  name?: string;
  address?: string;
  country: string;
  line1: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  state?: string;
}

export interface AdditionalFields {
  [key: string]: string;
}

export interface BillingDetailsT
  extends TaxIdT,
    AddressFieldsT,
    AdditionalFields {}

export interface CountriesOptionT {
  key: string;
  value: string;
  label: string;
  name: string;
}

// These are the intervals that are returned by Stripe, we only use month and year of these
export type StripeIntervalT = 'day' | 'week' | 'month' | 'year';

export type EducationalOrBusinessPlansHrefT = '/plans' | '/plans/education';
